import axios from 'axios';
import { handle401 } from '../apiAuth';

/*
 * Client for the Course Builder stack (coursebuilder.app.lxop.in,
 * docs/course-builder/CONTRACTS.md). Same token as the rest of the app;
 * link viewers also send X-Course-Link with the grant from GET /links/:token.
 */
export const BASE_URL =
  process.env.REACT_APP_COURSE_BUILDER_API_URL ||
  (typeof window !== 'undefined' && window.electronEnv?.courseBuilderApiUrl) ||
  'https://coursebuilder.app.lxop.in';

const client = axios.create({ baseURL: BASE_URL, timeout: 30000 });

// ── link grants ───────────────────────────────────────────────────────────────
/*
 * One active grant per course. It rides along only on requests about that
 * course, and only until it expires (2 h). Kept per tab in sessionStorage so a
 * reload does not spend another rate-limited link resolve.
 */
const grants = new Map(); // course_id -> { grant, expiresAt }
const GRANT_KEY = (token) => `lxops_aicourse_link_${token}`;

export function setLinkGrant(courseId, grant, expiresInSeconds = 7200) {
  if (!courseId || !grant) return;
  grants.set(courseId, { grant, expiresAt: Date.now() + Math.max(60, Number(expiresInSeconds) || 7200) * 1000 - 30000 });
}

export function clearLinkGrant(courseId) { grants.delete(courseId); }

export function activeLinkGrant(courseId) {
  const g = grants.get(courseId);
  if (!g) return null;
  if (g.expiresAt <= Date.now()) { grants.delete(courseId); return null; }
  return g.grant;
}

export function cacheLinkResolve(token, data) {
  try {
    sessionStorage.setItem(GRANT_KEY(token), JSON.stringify({
      course_id: data.course_id, link_grant: data.link_grant,
      expiresAt: Date.now() + Math.max(60, Number(data.expires_in) || 7200) * 1000 - 60000,
    }));
  } catch { /* storage unavailable */ }
}

export function dropCachedLinkResolve(token) {
  try { sessionStorage.removeItem(GRANT_KEY(token)); } catch { /* storage unavailable */ }
}

export function cachedLinkResolve(token) {
  try {
    const d = JSON.parse(sessionStorage.getItem(GRANT_KEY(token)) || 'null');
    if (!d || d.expiresAt <= Date.now()) return null;
    return d;
  } catch { return null; }
}

function courseIdOf(url) {
  const m = /\/ai-courses\/(?:view\/)?([0-9A-HJKMNP-TV-Z]{26})(?:\/|$|\?)/.exec(url || '');
  return m ? m[1] : null;
}

client.interceptors.request.use((config) => {
  const token = localStorage.getItem('lxops_token');
  if (token) config.headers.Authorization = `Bearer ${token}`;
  const cid = courseIdOf(config.url);
  const grant = cid ? activeLinkGrant(cid) : null;
  if (grant) config.headers['X-Course-Link'] = grant;
  return config;
});

client.interceptors.response.use(
  (res) => res,
  (err) => {
    // signup_required is the preview limit for visitors, not an expired session.
    if (err?.response?.data?.code !== 'signup_required') handle401(err);
    return Promise.reject(err);
  },
);

export default client;

// ── errors ────────────────────────────────────────────────────────────────────
const STATUS_CODE = {
  400: 'invalid', 401: 'unauthorized', 402: 'plan_required', 403: 'forbidden', 404: 'not_found',
  409: 'conflict', 410: 'account_deleted', 429: 'rate_limited',
};

export const ERROR_TEXT = {
  unauthorized: 'Sign in to continue.',
  signup_required: 'Create a free account to keep reading this course.',
  plan_required: 'This needs a Premium membership.',
  forbidden: 'You do not have access to this.',
  hidden: 'This course has been hidden while it is reviewed.',
  youtube_disabled: 'Videos are switched off for this course.',
  not_found: 'We could not find that course.',
  conflict: 'Someone else changed this first.',
  stale: 'This changed since you opened it.',
  claimed_elsewhere: 'Another device is already running this build.',
  account_deleted: 'This account has been deleted.',
  invalid: 'Some of that was not accepted. Check the highlighted fields.',
  rate_limited: 'Too many requests. Wait a minute and try again.',
  network: 'Could not reach LearnXops. Check your connection and try again.',
  server: 'Something went wrong on our side. Try again in a minute.',
};

/** { status, code, message, details, data } for any thrown request error. */
export function apiError(err) {
  const res = err?.response;
  if (!res) return { status: 0, code: 'network', message: ERROR_TEXT.network, details: null, data: null };
  const data = res.data && typeof res.data === 'object' ? res.data : {};
  const code = data.code || STATUS_CODE[res.status] || (res.status >= 500 ? 'server' : 'error');
  const said = typeof data.error === 'string' ? data.error.slice(0, 300) : '';
  // A 409 is a precondition the server words exactly ("Publish the course before
  // sharing it.", "Run the pre-publish checks first."). "Someone else changed
  // this first" is only the fallback when it sent no words.
  const serverWorded = code === 'conflict' && !!said;
  const message = (serverWorded && said) || ERROR_TEXT[code] || said || ERROR_TEXT.server;
  return { status: res.status, code, message, details: data.details || null, data, serverWorded };
}

/* Translated message for an error; `t` is react-i18next's. */
export function errorText(err, t) {
  const e = err && err.code && 'status' in err ? err : apiError(err);
  return typeof t === 'function' && !e.serverWorded ? t(`courseBuilder.errors.api.${e.code}`, e.message) : e.message;
}

const enc = encodeURIComponent;
const d = (p) => p.then((r) => r.data);

// ── config, account, library ──────────────────────────────────────────────────
export const getConfig = () => d(client.get('/ai-courses/config'));
export const getEntitlement = () => d(client.get('/ai-courses/entitlement'));
export const getTerms = () => d(client.get('/ai-courses/terms'));
export const acceptTerms = (version) => d(client.post('/ai-courses/terms', { version }));
export const getLabsCatalog = () => d(client.get('/ai-courses/labs/catalog'));
export const getMine = (tab = 'my', cursor) => d(client.get('/ai-courses/mine', { params: { tab, ...(cursor ? { cursor } : {}) } }));
export const getLearning = () => d(client.get('/ai-courses/learning'));

// ── course and build lifecycle ────────────────────────────────────────────────
export const createCourse = (questionnaire) => d(client.post('/ai-courses', { questionnaire }));
export const getRequest = (cid) => d(client.get(`/ai-courses/${enc(cid)}/request`));
export const putRequest = (cid, questionnaire) => d(client.put(`/ai-courses/${enc(cid)}/request`, { questionnaire }));
export const patchCourse = (cid, body) => d(client.patch(`/ai-courses/${enc(cid)}`, body));
export const deleteCourse = (cid) => d(client.delete(`/ai-courses/${enc(cid)}`));
export const createBuild = (cid, body) => d(client.post(`/ai-courses/${enc(cid)}/builds`, body));
export const getBuild = (cid, bid) => d(client.get(`/ai-courses/${enc(cid)}/builds/${enc(bid)}`));
export const cancelBuild = (cid, bid) => d(client.post(`/ai-courses/${enc(cid)}/builds/${enc(bid)}/cancel`, {}));
export const getBuildInputs = (cid, bid) => d(client.get(`/ai-courses/${enc(cid)}/builds/${enc(bid)}/inputs`));
export const putClarify = (cid, bid, body) => d(client.put(`/ai-courses/${enc(cid)}/builds/${enc(bid)}/inputs/clarify`, body));
export const putOutlineInput = (cid, bid, body) => d(client.put(`/ai-courses/${enc(cid)}/builds/${enc(bid)}/inputs/outline`, body));

// ── draft ─────────────────────────────────────────────────────────────────────
export const getDraft = (cid) => d(client.get(`/ai-courses/${enc(cid)}/draft`));
export const getDraftQuality = (cid) => d(client.get(`/ai-courses/${enc(cid)}/draft/quality`));
export const getDraftLesson = (cid, lid) => d(client.get(`/ai-courses/${enc(cid)}/draft/lessons/${enc(lid)}`));
export const putDraftLesson = (cid, lid, body) => d(client.put(`/ai-courses/${enc(cid)}/draft/lessons/${enc(lid)}`, body));
export const deleteDraftLesson = (cid, lid) => d(client.delete(`/ai-courses/${enc(cid)}/draft/lessons/${enc(lid)}`));
export const putDraftOutline = (cid, body) => d(client.put(`/ai-courses/${enc(cid)}/draft/outline`, body));
export const getDraftQuiz = (cid, mid) => d(client.get(`/ai-courses/${enc(cid)}/draft/quizzes/${enc(mid)}`));
export const putDraftQuiz = (cid, mid, body) => d(client.put(`/ai-courses/${enc(cid)}/draft/quizzes/${enc(mid)}`, body));
export const getDraftCapstone = (cid) => d(client.get(`/ai-courses/${enc(cid)}/draft/capstone`));
export const putDraftCapstone = (cid, body) => d(client.put(`/ai-courses/${enc(cid)}/draft/capstone`, body));
export const getDraftSources = (cid) => d(client.get(`/ai-courses/${enc(cid)}/draft/sources`));
export const lessonAction = (cid, lid, body) => d(client.post(`/ai-courses/${enc(cid)}/draft/lessons/${enc(lid)}/actions`, body));

// ── publishing, visibility, versions, sharing ─────────────────────────────────
export const createCheck = (cid, target) => d(client.post(`/ai-courses/${enc(cid)}/checks`, { target }));
export const getCheck = (cid, checkId) => d(client.get(`/ai-courses/${enc(cid)}/checks/${enc(checkId)}`));
export const publish = (cid, body) => d(client.post(`/ai-courses/${enc(cid)}/publish`, body));
export const setVisibility = (cid, body) => d(client.put(`/ai-courses/${enc(cid)}/visibility`, body));
export const createLink = (cid, audience) => d(client.post(`/ai-courses/${enc(cid)}/link`, { audience }));
export const revokeLink = (cid) => d(client.delete(`/ai-courses/${enc(cid)}/link`));
export const getVersions = (cid) => d(client.get(`/ai-courses/${enc(cid)}/versions`));
export const rollback = (cid, v, changelog) => d(client.post(`/ai-courses/${enc(cid)}/versions/${enc(v)}/rollback`, changelog ? { changelog } : {}));
export const getShares = (cid) => d(client.get(`/ai-courses/${enc(cid)}/shares`));
export const addShare = (cid, body) => d(client.post(`/ai-courses/${enc(cid)}/shares`, body));
export const patchShare = (cid, sub, body) => d(client.patch(`/ai-courses/${enc(cid)}/shares/${enc(sub)}`, body));
export const removeShare = (cid, sub) => d(client.delete(`/ai-courses/${enc(cid)}/shares/${enc(sub)}`));
export const forkCourse = (cid, customize = false) => d(client.post(`/ai-courses/${enc(cid)}/fork`, { customize }));
export const exportCourse = (cid, version) => d(client.get(`/ai-courses/${enc(cid)}/export`, { params: { format: 'md', ...(version ? { version } : {}) } }));
export const getStats = (cid) => d(client.get(`/ai-courses/${enc(cid)}/stats`));

// ── viewing ───────────────────────────────────────────────────────────────────
export const getCatalog = (params = {}) => d(client.get('/ai-courses/catalog', { params }));
export const getFeatured = () => d(client.get('/ai-courses/featured'));
export const getBySlug = (slug) => d(client.get(`/ai-courses/by-slug/${enc(slug)}`));
export const getView = (cid) => d(client.get(`/ai-courses/view/${enc(cid)}`));
export const getViewLesson = (cid, lid) => d(client.get(`/ai-courses/view/${enc(cid)}/lessons/${enc(lid)}`));
export const getViewQuiz = (cid, mid) => d(client.get(`/ai-courses/view/${enc(cid)}/quizzes/${enc(mid)}`));
export const getViewCapstone = (cid) => d(client.get(`/ai-courses/view/${enc(cid)}/capstone`));
export const resolveLink = (token) => d(client.get(`/ai-courses/links/${enc(token)}`));
export const getProgress = (cid) => d(client.get(`/ai-courses/view/${enc(cid)}/progress`));
export const putProgress = (cid, body) => d(client.put(`/ai-courses/view/${enc(cid)}/progress`, body));
export const getRatings = (cid) => d(client.get(`/ai-courses/view/${enc(cid)}/ratings`));
export const putRating = (cid, body) => d(client.put(`/ai-courses/view/${enc(cid)}/rating`, body));
export const deleteRating = (cid) => d(client.delete(`/ai-courses/view/${enc(cid)}/rating`));
export const replyToRating = (cid, ratingId, text) => d(client.post(`/ai-courses/view/${enc(cid)}/ratings/${enc(ratingId)}/reply`, { text }));
export const reportCourse = (cid, body) => d(client.post(`/ai-courses/view/${enc(cid)}/reports`, body));
